import { idSelectionSchema, keysetPageInputSchema, keysetPageSchema, sortSchema } from "@amtana/module";
import { z } from "zod";

// The organization is the tenant (ADR-001 §1): the row every other module will
// key its data on. S1.01 gave it only what it needs to exist and be renamed;
// S3.01 adds who belongs to it, and S1.02 the sign-up hook that opens one.

export const ORGANIZATION_NAME_MAX = 120;

export const organizationNameSchema = z.string().trim().min(1).max(ORGANIZATION_NAME_MAX);

// Deleting with a grace period (S1.06): "active" is the only state before
// this story ever wrote a row, so every organization that already existed
// defaults to it. `pending_deletion` starts a countdown instead of an
// immediate purge, so an admin can undo it inside the window; a scheduled
// task purges for good once it passes (D127 in dev/docs/decisions.md).
export const ORGANIZATION_STATUSES = ["active", "pending_deletion"] as const;
export const organizationStatusSchema = z.enum(ORGANIZATION_STATUSES);
export type OrganizationStatus = (typeof ORGANIZATION_STATUSES)[number];

export const organizationSchema = z.object({
  id: z.string(),
  name: organizationNameSchema,
  /** URL-safe, unique, derived from the name. */
  slug: z.string(),
  /** The person who opened it; the author, not necessarily a member (see `Membership`). */
  createdBy: z.string(),
  createdAt: z.iso.datetime(),
  /** A small picture, checked and decoded by `@amtana/media` first (S1.06). */
  logoUrl: z.string().nullable(),
  status: organizationStatusSchema,
  deletionRequestedAt: z.iso.datetime().nullable(),
  /** When the grace period ends and the purge task may act; null unless `status` is
   * `pending_deletion`. */
  deletionPurgeAt: z.iso.datetime().nullable(),
  /** The seat cap billing's own plan and purchased extra seats add up to right now
   * (S3.07, S5.12); null means unlimited. Billing has no peer back to this module (it
   * would close a cycle with this module's own peer on billing), so it pushes this
   * number here instead, through `setSeatAllowance`, whenever it changes. Defaults to
   * the free plan's included seats at creation, before billing ever pushes anything. */
  seatCap: z.number().int().nonnegative().nullable(),
});
export type Organization = z.infer<typeof organizationSchema>;

export const createOrganizationInputSchema = z.object({ name: organizationNameSchema });
export const getOrganizationInputSchema = z.object({ organizationId: z.string() });
export const renameOrganizationInputSchema = z.object({
  organizationId: z.string(),
  name: organizationNameSchema,
});

export type CreateOrganizationInput = z.infer<typeof createOrganizationInputSchema>;
export type GetOrganizationInput = z.infer<typeof getOrganizationInputSchema>;
export type RenameOrganizationInput = z.infer<typeof renameOrganizationInputSchema>;

/** A data URL upload, the same shape account's avatar takes (S1.06); `dataUrl: null`
 * removes it instead, so setting and clearing share one door. */
export const setOrganizationLogoInputSchema = z.object({
  organizationId: z.string(),
  dataUrl: z.string().min(1, "Choose an image.").nullable(),
});
export type SetOrganizationLogoInput = z.infer<typeof setOrganizationLogoInputSchema>;

export const requestOrganizationDeletionInputSchema = z.object({ organizationId: z.string() });
export type RequestOrganizationDeletionInput = z.infer<typeof requestOrganizationDeletionInputSchema>;

/** Restoring is admin-only, enforced in the service itself rather than the usual
 * permission door, since a pending-deletion organization carries no live permissions
 * for anyone to hold (see `restoreOrganization` on `OrganizationApi`). */
export const restoreOrganizationInputSchema = z.object({ organizationId: z.string() });
export type RestoreOrganizationInput = z.infer<typeof restoreOrganizationInputSchema>;

/** Sign-up (S1.02): the person's name becomes the organization's (ADR-003 §3). */
export const createPersonalOrganizationInputSchema = z.object({
  firstName: z.string().trim().min(1).max(ORGANIZATION_NAME_MAX),
  lastName: z.string().trim().max(ORGANIZATION_NAME_MAX),
});
export type CreatePersonalOrganizationInput = z.infer<typeof createPersonalOrganizationInputSchema>;

// Membership (S3.01, ADR-002 §1): who belongs to an organization, their role and
// whether they are disabled. The three roles mirror `@amtana/access`'s `ROLES`
// (ADR-002); a contract may depend only on zod and @amtana/module (D017), so the
// list is restated here rather than imported.
export const MEMBERSHIP_ROLES = ["admin", "member", "viewer"] as const;
export const membershipRoleSchema = z.enum(MEMBERSHIP_ROLES);
export type MembershipRole = (typeof MEMBERSHIP_ROLES)[number];

export const membershipSchema = z.object({
  organizationId: z.string(),
  userId: z.string(),
  role: membershipRoleSchema,
  disabled: z.boolean(),
  joinedAt: z.iso.datetime(),
});
export type Membership = z.infer<typeof membershipSchema>;

export const addMemberInputSchema = z.object({
  organizationId: z.string(),
  userId: z.string(),
  role: membershipRoleSchema,
});
export type AddMemberInput = z.infer<typeof addMemberInputSchema>;

export const setMemberDisabledInputSchema = z.object({
  organizationId: z.string(),
  userId: z.string(),
  disabled: z.boolean(),
});
export type SetMemberDisabledInput = z.infer<typeof setMemberDisabledInputSchema>;

export const membershipsForUserInputSchema = z.object({ userId: z.string() });
export type MembershipsForUserInput = z.infer<typeof membershipsForUserInputSchema>;

/** How many accounts the one-time backfill (S1.02) gave a personal organization just now. */
export const backfillPersonalOrganizationsOutputSchema = z.object({
  created: z.number().int().nonnegative(),
});
export type BackfillPersonalOrganizationsOutput = z.infer<typeof backfillPersonalOrganizationsOutputSchema>;

// The members directory (S3.02): the list an admin manages and everyone
// else reads, and the three ways it changes a membership, each guarded
// against ever leaving the organization without an admin (S3.03).

/** A membership with the person's name and email, for the directory. */
export const memberViewSchema = membershipSchema.extend({
  name: z.string(),
  email: z.email(),
});
export type MemberView = z.infer<typeof memberViewSchema>;

export const membersInputSchema = z.object({});
export const membersOutputSchema = z.object({
  members: z.array(memberViewSchema),
  /** The caller's own role in this organization, so the screen knows whether to offer
   * the role picker, remove and disable, or show the roster read-only. */
  callerRole: membershipRoleSchema,
});
export type MembersOutput = z.infer<typeof membersOutputSchema>;

export const setMemberRoleInputSchema = z.object({
  organizationId: z.string(),
  userId: z.string(),
  role: membershipRoleSchema,
});
export type SetMemberRoleInput = z.infer<typeof setMemberRoleInputSchema>;

export const removeMemberInputSchema = z.object({
  organizationId: z.string(),
  userId: z.string(),
});
export type RemoveMemberInput = z.infer<typeof removeMemberInputSchema>;

export const leaveOrganizationInputSchema = z.object({});
export type LeaveOrganizationInput = z.infer<typeof leaveOrganizationInputSchema>;

/** A plain acknowledgement, for a mutation with nothing else to answer. */
export const okSchema = z.object({ ok: z.literal(true) });
export type Ok = z.infer<typeof okSchema>;

// S1.03: every other module's backfill reads the organization a row's owner
// belongs to from here, instead of opening membership rows of its own.
export const organizationIdsForUsersInputSchema = z.object({ userIds: z.array(z.string()) });
export type OrganizationIdsForUsersInput = z.infer<typeof organizationIdsForUsersInputSchema>;

/** `userId` to their active organization's id; a user with no membership is left out. */
export const organizationIdsForUsersOutputSchema = z.record(z.string(), z.string());
export type OrganizationIdsForUsersOutput = z.infer<typeof organizationIdsForUsersOutputSchema>;

// Invitations (S3.04, S3.05, S3.06): an admin invites an address with a role; the
// secret itself is never stored (see the invitation repository), only its hash, so
// this schema never carries it either. `expired` is read against the clock when the
// invitation is answered, not a status written at the time it happens to pass.

// "held" (S3.07): a seat was full when this invite was asked for, so it carries
// a token but was never mailed; it becomes "pending" (and is mailed) once a
// seat frees or the organization's seat cap grows.
export const INVITATION_STATUSES = ["pending", "held", "accepted", "revoked", "declined"] as const;
export const invitationStatusSchema = z.enum(INVITATION_STATUSES);
export type InvitationStatus = (typeof INVITATION_STATUSES)[number];

export const invitationSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  email: z.email(),
  role: membershipRoleSchema,
  status: invitationStatusSchema,
  invitedByUserId: z.string(),
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  lastSentAt: z.iso.datetime(),
  expired: z.boolean(),
});
export type Invitation = z.infer<typeof invitationSchema>;

export const inviteMemberInputSchema = z.object({
  organizationId: z.string(),
  email: z.email(),
  role: membershipRoleSchema,
});
export type InviteMemberInput = z.infer<typeof inviteMemberInputSchema>;

/** The invite link, minted fresh by whichever call just asked for one (invite, resend
 * or copy); never stored, so this is the only time a caller ever sees it. */
export const invitationLinkOutputSchema = z.object({
  invitation: invitationSchema,
  link: z.string(),
});
export type InvitationLinkOutput = z.infer<typeof invitationLinkOutputSchema>;

export const invitationsInputSchema = z.object({});
export const invitationsOutputSchema = z.object({ invitations: z.array(invitationSchema) });
export type InvitationsOutput = z.infer<typeof invitationsOutputSchema>;

export const invitationIdInputSchema = z.object({
  organizationId: z.string(),
  id: z.string(),
});
export type InvitationIdInput = z.infer<typeof invitationIdInputSchema>;

/** `OrganizationApi.mintInviteLink`'s own input (S26.13, ADR-022 point 7): `id` is the
 * invitation's id, the same shape every other invitation-id door takes. */
export const mintInviteLinkInputSchema = z.object({
  organizationId: z.string(),
  id: z.string(),
});
export type MintInviteLinkInput = z.infer<typeof mintInviteLinkInputSchema>;

export const mintedInviteLinkSchema = z.object({ link: z.string().min(1) });
export type MintedInviteLink = z.infer<typeof mintedInviteLinkSchema>;

export const acceptInviteInputSchema = z.object({ token: z.string() });
export type AcceptInviteInput = z.infer<typeof acceptInviteInputSchema>;

export const acceptInviteOutputSchema = z.object({
  organizationId: z.string(),
  role: membershipRoleSchema,
});
export type AcceptInviteOutput = z.infer<typeof acceptInviteOutputSchema>;

export const declineInviteInputSchema = z.object({ token: z.string() });
export type DeclineInviteInput = z.infer<typeof declineInviteInputSchema>;

// The switcher (S1.05): every organization a person belongs to, enough to
// render and switch between, and which one the request just resolved as
// active (undefined when none resolved: a disabled membership, or a
// pending-deletion organization carrying no live permissions, see
// `withPermissions` in organization.module.ts).
export const myOrganizationSchema = z.object({
  organizationId: z.string(),
  name: organizationNameSchema,
  slug: z.string(),
  role: membershipRoleSchema,
  status: organizationStatusSchema,
  logoUrl: z.string().nullable(),
  deletionPurgeAt: z.iso.datetime().nullable(),
});
export type MyOrganization = z.infer<typeof myOrganizationSchema>;

export const myOrganizationsInputSchema = z.object({});
export const myOrganizationsOutputSchema = z.object({
  organizations: z.array(myOrganizationSchema),
  activeOrganizationId: z.string().nullable(),
});
export type MyOrganizationsOutput = z.infer<typeof myOrganizationsOutputSchema>;

// The back office (S9.03, ADR-005 point 4): what this module owns about every organization,
// across tenants, never a customer's content. Plan, seats used and credit balance are
// billing's own facts (`BillingApi.overview`), read separately by whoever composes the list.
export const organizationSummarySchema = z.object({
  organizationId: z.string(),
  name: organizationNameSchema,
  slug: z.string(),
  status: organizationStatusSchema,
  createdAt: z.iso.datetime(),
  memberCount: z.number().int().nonnegative(),
});
export type OrganizationSummary = z.infer<typeof organizationSummarySchema>;

export const listOrganizationsInputSchema = z.object({ query: z.string().trim().optional() });
export type ListOrganizationsInput = z.infer<typeof listOrganizationsInputSchema>;
export const listOrganizationsOutputSchema = z.object({ organizations: z.array(organizationSummarySchema) });
export type ListOrganizationsOutput = z.infer<typeof listOrganizationsOutputSchema>;

/** What the back office can order every organization by (ADR-033): name, opening, live members. */
export const ORGANIZATION_SORT_FIELDS = ["name", "createdAt", "members"] as const;
export type OrganizationSortField = (typeof ORGANIZATION_SORT_FIELDS)[number];
export const organizationSortSchema = sortSchema(ORGANIZATION_SORT_FIELDS);
export type OrganizationSort = z.infer<typeof organizationSortSchema>;

/** One keyset page of every organization, narrowed by a name or slug and by an id selection a
 * peer computed from its own facts (billing's plan and status, ADR-033 point 3). */
export const pageOrganizationsInputSchema = keysetPageInputSchema.extend({
  query: z.string().trim().max(200).optional(),
  selection: idSelectionSchema.optional(),
  sort: organizationSortSchema.optional(),
});
export type PageOrganizationsInput = z.infer<typeof pageOrganizationsInputSchema>;
export const organizationPageSchema = keysetPageSchema(organizationSummarySchema);
export type OrganizationPage = z.infer<typeof organizationPageSchema>;

/** Unlike `members`, the caller need not belong to the organization (S9.03): the back
 * office's own detail screen, state never content. */
export const organizationMembersInputSchema = z.object({ organizationId: z.string() });
export type OrganizationMembersInput = z.infer<typeof organizationMembersInputSchema>;
export const organizationMembersOutputSchema = z.object({ members: z.array(memberViewSchema) });
export type OrganizationMembersOutput = z.infer<typeof organizationMembersOutputSchema>;
